import { useMemo } from "react";

import { useDispatch } from "react-redux";

import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  useResetAccessConfig,
  useSetAccessConfig,
} from "@hooks/mutations/use-access-config";
import {
  useAccessConfig,
  useAccessConfigCatalogue,
} from "@hooks/queries/use-access-config";
import { useMe } from "@hooks/queries/use-me";

import { notifyError } from "@utils/notification";

/** Per-user-type view model: which capabilities are enabled + is-default flag. */
type TypeState = { enabled: Set<string>; isDefault: boolean };

const AccessConfigurationPage = () => {
  const dispatch = useDispatch();
  const { data: me, isLoading: meLoading } = useMe();
  const isAdmin = Boolean(me?.user?.is_admin);

  const { data: catalogue, isLoading: catLoading } = useAccessConfigCatalogue({
    enabled: isAdmin,
  });
  const { data: rows = [], isLoading: rowsLoading } = useAccessConfig({
    enabled: isAdmin,
  });

  const setConfig = useSetAccessConfig();
  const resetConfig = useResetAccessConfig();
  const isSaving = setConfig.isPending || resetConfig.isPending;

  // type -> {enabled set, isDefault}, seeded from the server rows.
  const byType = useMemo(() => {
    const map = new Map<string, TypeState>();
    for (const row of rows) {
      map.set(row.user_type, {
        enabled: new Set(row.capabilities),
        isDefault: row.is_default,
      });
    }
    return map;
  }, [rows]);

  if (meLoading) {
    return <div className="px-4 py-4 text-foreground-muted">Loading…</div>;
  }
  if (!isAdmin) {
    return (
      <div className="px-4 py-4 text-foreground-muted">
        You don’t have access to this page.
      </div>
    );
  }

  const capabilities = catalogue?.capabilities ?? [];
  const userTypes = catalogue?.user_types ?? [];
  const isLoadingTable = catLoading || rowsLoading;

  const toggle = async (userType: string, capability: string, next: boolean) => {
    const current = byType.get(userType)?.enabled ?? new Set<string>();
    const updated = new Set(current);
    if (next) updated.add(capability);
    else updated.delete(capability);
    try {
      await setConfig.mutateAsync({ userType, capabilities: Array.from(updated) });
    } catch (error) {
      // The switch itself reflects server-cached state (see byType above), so a
      // failed save already reverts it visually — this only supplies the
      // feedback explaining why nothing changed (MDRS-437).
      notifyError(error, dispatch, "Failed to update capability access.");
    }
  };

  const resetToDefault = async (userType: string) => {
    try {
      await resetConfig.mutateAsync({ userType });
    } catch (error) {
      notifyError(error, dispatch, "Failed to reset access to default.");
    }
  };

  return (
    <div className="space-y-2 p-2 pl-0">
      <div className="rounded-2xl border border-foreground-muted/20 bg-background-white p-4">
        <PageHeader
          title="Access Configuration"
          description={
            <>
              Turn parts of the system on or off per user type. Changes apply to
              every user of that type; admins always have full access. A type left on{" "}
              <strong>Default</strong> uses the built-in access level.
            </>
          }
        />
      </div>

      <div className="overflow-hidden rounded-2xl border border-foreground-muted/20 bg-background-white">
        <div className="flex flex-wrap items-center gap-2 p-4">
          <h2 className="text-base font-semibold tracking-[-0.01em]">
            Capabilities
          </h2>
          <Badge variant="secondary">{capabilities.length}</Badge>
        </div>

        {isLoadingTable ? (
          <div className="px-4 pb-4 text-foreground-muted">Loading…</div>
        ) : (
          <Table className="border-t border-foreground-muted/20">
            <TableHeader>
              <TableRow>
                <TableHead>Page / feature</TableHead>
                {userTypes.map((t) => (
                  <TableHead key={t.value} className="text-center">
                    <div className="flex flex-col items-center gap-1.5 py-1">
                      <span>{t.label}</span>
                      {byType.get(t.value)?.isDefault ? (
                        <Badge variant="secondary">Default</Badge>
                      ) : (
                        <Badge variant="success">Customised</Badge>
                      )}
                      <Button
                        variant="simple-outline"
                        size="sm"
                        disabled={isSaving || byType.get(t.value)?.isDefault}
                        onClick={() => resetToDefault(t.value)}
                      >
                        Reset to default
                      </Button>
                    </div>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {capabilities.map((cap) => (
                <TableRow key={cap.value} className="hover:bg-[#E8FDFF]/50">
                  <TableCell className="font-medium">{cap.label}</TableCell>
                  {userTypes.map((t) => {
                    const enabled =
                      byType.get(t.value)?.enabled.has(cap.value) ?? false;
                    return (
                      <TableCell key={t.value} className="text-center">
                        <Switch
                          checked={enabled}
                          disabled={isSaving}
                          aria-label={`${t.label}: ${cap.label}`}
                          onCheckedChange={(next) =>
                            toggle(t.value, cap.value, next)
                          }
                        />
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
};

export default AccessConfigurationPage;
